import { useState, useEffect, useCallback } from 'react';
import { collection, query, onSnapshot, doc, updateDoc, writeBatch, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import type { SystemUser, EmployeeData } from '@monthly-data-collection/shared';
import { programAssignmentService, type ProgramAssignment } from '@/services/setup/programAssignmentService';
import { workflowConfigService, type WorkflowConfig } from '@/services/shared/workflowConfigService';
import type { UserRole } from '@monthly-data-collection/shared';

export interface SystemUserWithEmployeeData extends SystemUser {
  employee_data?: EmployeeData;
  employee_setup?: any; // Additional setup data from Firestore
}

interface UserSearchFilters {
  searchQuery?: string;
  team?: string;
  role?: string;
  associationStatus?: 'pending' | 'associated' | 'manual';
  permissions?: {
    canAdmin?: boolean;
    canSetup?: boolean;
    canReview?: boolean;
    canCollaborate?: boolean;
  };
}

/**
 * Group membership and the super-admin flag are written only by the
 * updateUserAccess Cloud Function (super-admin-gated, audited). Firestore
 * rules reject direct client writes to those two fields.
 */
interface UpdateUserAccessRequest {
  userId: string;
  groups?: string[];
  isSuperAdmin?: boolean;
}

const updateUserAccess = httpsCallable<UpdateUserAccessRequest, UpdateUserAccessRequest>(
  functions,
  'updateUserAccess',
);

interface BulkPermissionUpdate {
  userIds: string[];
  permissions: Partial<SystemUser['permissions']>;
}

interface BulkRoleUpdate {
  userIds: string[];
  role: UserRole;
}

interface UseSystemUsersResult {
  users: SystemUserWithEmployeeData[];
  loading: boolean;
  error: string | null;
  totalUsers: number;
  filteredUsers: SystemUserWithEmployeeData[];
  
  // Search and filter
  searchUsers: (filters: UserSearchFilters) => void;
  clearFilters: () => void;
  currentFilters: UserSearchFilters;
  
  // User operations
  updateUserPermissions: (userId: string, permissions: Partial<SystemUser['permissions']>) => Promise<void>;
  updateUserRole: (userId: string, role: UserRole) => Promise<void>;
  updateUserGroups: (userId: string, groupIds: string[]) => Promise<void>;
  updateSuperAdmin: (userId: string, isSuperAdmin: boolean) => Promise<void>;
  bulkUpdatePermissions: (update: BulkPermissionUpdate) => Promise<void>;
  bulkUpdateRoles: (update: BulkRoleUpdate) => Promise<void>;
  associateUser: (userId: string, firebaseUid: string) => Promise<void>;
  markDataCollectionComplete: (userId: string, isComplete: boolean) => Promise<void>;
  
  // Starred workflows
  toggleStarredWorkflow: (userId: string, workflowId: string) => Promise<void>;
  
  // Starred users
  toggleStarredUser: (userId: string, starredUserId: string) => Promise<void>;
  
  // Starred teams
  toggleStarredTeam: (userId: string, teamId: string) => Promise<void>;
  
  // CRM saved views
  updateCrmSavedView: (userId: string, filters: any[]) => Promise<void>;
  updateCrmSavedViewPeople: (userId: string, filters: any[]) => Promise<void>;
  
  // Statistics
  statistics: {
    totalUsers: number;
    associatedUsers: number;
    pendingUsers: number;
    manualUsers: number;
    adminUsers: number;
    managerGroupUsers: number;
    leadershipUsers: number;
  };
}

export function useSystemUsers(): UseSystemUsersResult {
  const [users, setUsers] = useState<SystemUserWithEmployeeData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentFilters, setCurrentFilters] = useState<UserSearchFilters>({});
  const [filteredUsers, setFilteredUsers] = useState<SystemUserWithEmployeeData[]>([]);
  const [programAssignments, setProgramAssignments] = useState<ProgramAssignment[]>([]);
  const [workflowConfigs, setWorkflowConfigs] = useState<WorkflowConfig[]>([]);

  // Load program assignments with real-time updates
  useEffect(() => {
    const unsubscribeProgramAssignments = programAssignmentService.subscribeToAllProgramAssignments(
      (assignments) => {
        setProgramAssignments(assignments);
      }
    );

    return () => unsubscribeProgramAssignments();
  }, []);

  // Load workflow configs to determine workflow assignments
  useEffect(() => {
    const loadWorkflowConfigs = async () => {
      try {
        const configs = await workflowConfigService.getAllWorkflowConfigs();
        setWorkflowConfigs(configs);
      } catch (error) {
        console.error('Error loading workflow configs for user assignments:', error);
      }
    };

    loadWorkflowConfigs();
  }, []);

  // Load system users with real-time updates and merge with program assignments
  useEffect(() => {
    // Query active system users only for Admin page (status is in employee_data)
    const systemUsersQuery = query(
      collection(db, 'system_users'),
      where('employee_data.status', '==', 'active')
    );

    const unsubscribe = onSnapshot(
      systemUsersQuery,
      (snapshot) => {
        try {
          const usersData: SystemUserWithEmployeeData[] = [];
          
          // Create a map of user ID to their program assignments
          const userProgramMap = new Map<string, ProgramAssignment[]>();
          programAssignments.forEach(assignment => {
            assignment.assignedUsers.forEach(userId => {
              if (!userProgramMap.has(userId)) {
                userProgramMap.set(userId, []);
              }
              userProgramMap.get(userId)!.push(assignment);
            });
          });

          // Create a map of user ID to their workflow assignments
          const userWorkflowMap = new Map<string, number[]>();
          workflowConfigs.forEach(config => {
            if (config.allocation_config) {
              const assignedUsers: string[] = [];
              const allocation = config.allocation_config;
              
              // Collect all users assigned to this workflow
              if (allocation.team_lead) assignedUsers.push(allocation.team_lead);
              if (allocation.support_1) assignedUsers.push(allocation.support_1);
              if (allocation.support_2) assignedUsers.push(allocation.support_2);
              if (allocation.manager_override && Array.isArray(allocation.manager_override)) {
                assignedUsers.push(...allocation.manager_override);
              }
              
              // Map users to workflows
              assignedUsers.forEach(userId => {
                if (userId && userId.trim()) {
                  if (!userWorkflowMap.has(userId)) {
                    userWorkflowMap.set(userId, []);
                  }
                  userWorkflowMap.get(userId)!.push(parseInt(config.workflowId));
                }
              });
            }
          });
          
          snapshot.forEach((doc) => {
            const data = doc.data();
            const userId = String(data.employee_id); // Use employee_id to match with assignments
            const userPrograms = userProgramMap.get(userId) || [];
            const userWorkflows = userWorkflowMap.get(userId) || [];
            
            
            // For Admin page, show ALL users in system_users collection
            const systemUser: SystemUserWithEmployeeData = {
              id: doc.id,
              employee_id: data.employee_id,
              firebase_uid: data.firebase_uid,
              email: data.email,
              role: data.role || 'contributor', // Default to contributor if role not set
              permissions: data.permissions || {
                canAdmin: false,
                canSetup: false,
                canReview: false,
                canCollaborate: true
              },
              // Populate assigned_workflows with workflow IDs from workflow configs
              assigned_workflows: userWorkflows,
              // Populate program_memberships with program IDs (as per SystemUser interface)
              program_memberships: userPrograms.map(program => program.programId),
              // Populate starred_workflows from Firestore
              starred_workflows: data.starred_workflows || [],
              // Populate starred_users from Firestore
              starred_users: data.starred_users || [],
              // Populate starred_teams from Firestore
              starred_teams: data.starred_teams || [],
              // Add CRM saved views from Firestore
              crm_saved_view: data.crm_saved_view,
              crm_saved_view_people: data.crm_saved_view_people,
              // Add Asana user GID from Firestore
              asana_user_gid: data.asana_user_gid,
              asana_synced_at: data.asana_synced_at,
              // Group-based access control
              groups: data.groups || [],
              isSuperAdmin: data.isSuperAdmin || false,
              // Add Slack user ID from Firestore
              slack_user_id: data.slack_user_id,
              slack_synced_at: data.slack_synced_at,
              // Set association status based on firebase_uid presence
              association_status: data.firebase_uid ? 'associated' : (data.association_status || 'pending'),
              // Explicitly map data collection completion fields
              data_collection_complete: data.data_collection_complete || false,
              data_collection_completed_at: data.data_collection_completed_at,
              created_at: data.created_at,
              updated_at: data.updated_at,
              created_by: data.created_by,
              employee_data: data.employee_data,
              employee_setup: data.employee_setup
            };
            usersData.push(systemUser);
          });

          // Sort users by name on the client side
          usersData.sort((a, b) => {
            const nameA = a.employee_data?.name || '';
            const nameB = b.employee_data?.name || '';
            return nameA.localeCompare(nameB);
          });

          setUsers(usersData);
          setLoading(false);
          setError(null);
        } catch (err) {
          console.error('Error processing system users:', err);
          setError(err instanceof Error ? err.message : 'Failed to load users');
          setLoading(false);
        }
      },
      (err) => {
        console.error('Error loading system users:', err);
        setError(err.message || 'Failed to load users');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [programAssignments, workflowConfigs]); // Re-run when program assignments or workflow configs change

  // Apply filters to users
  useEffect(() => {
    let filtered = [...users];

    // Search query filter with null/undefined safety
    if (currentFilters.searchQuery) {
      const query = currentFilters.searchQuery.toLowerCase();
      filtered = filtered.filter(user =>
        (user.employee_data?.name?.toLowerCase() || '').includes(query) ||
        (user.email?.toLowerCase() || '').includes(query) ||
        (user.employee_data?.team_name?.toLowerCase() || '').includes(query) ||
        (user.employee_data?.role_name?.toLowerCase() || '').includes(query)
      );
    }

    // Team filter
    if (currentFilters.team) {
      filtered = filtered.filter(user => 
        user.employee_data?.team_name === currentFilters.team
      );
    }

    // Role filter
    if (currentFilters.role) {
      filtered = filtered.filter(user => 
        user.employee_data?.role_name === currentFilters.role
      );
    }

    // Association status filter
    if (currentFilters.associationStatus) {
      filtered = filtered.filter(user => 
        user.association_status === currentFilters.associationStatus
      );
    }

    // Permission filters
    if (currentFilters.permissions) {
      const permFilters = currentFilters.permissions;
      filtered = filtered.filter(user => {
        if (!user.permissions) return false;
        return Object.entries(permFilters).every(([key, value]) => {
          if (value === undefined) return true;
          return user.permissions?.[key as keyof SystemUser['permissions']] === value;
        });
      });
    }

    setFilteredUsers(filtered);
  }, [users, currentFilters]);

  // Search and filter functions
  const searchUsers = useCallback((filters: UserSearchFilters) => {
    setCurrentFilters(filters);
  }, []);

  const clearFilters = useCallback(() => {
    setCurrentFilters({});
  }, []);

  // Update user permissions
  const updateUserPermissions = useCallback(async (userId: string, permissions: Partial<SystemUser['permissions']>) => {
    try {
      const userRef = doc(db, 'system_users', userId);
      const user = users.find(u => u.id === userId);
      
      if (!user) {
        throw new Error('User not found');
      }

      const updatedPermissions = {
        ...user.permissions,
        ...permissions
      };

      await updateDoc(userRef, {
        permissions: updatedPermissions,
        updated_at: new Date()
      });

    } catch (err) {
      console.error('Error updating user permissions:', err);
      throw new Error(err instanceof Error ? err.message : 'Failed to update permissions');
    }
  }, [users]);

  // Update user role directly
  const updateUserRole = useCallback(async (userId: string, role: UserRole) => {
    try {
      const userRef = doc(db, 'system_users', userId);
      
      await updateDoc(userRef, {
        role: role,
        updated_at: new Date()
      });

    } catch (err) {
      console.error('Error updating user role:', err);
      throw new Error(err instanceof Error ? err.message : 'Failed to update role');
    }
  }, []);

  // Update user group assignments
  const updateUserGroups = useCallback(async (userId: string, groupIds: string[]) => {
    try {
      await updateUserAccess({ userId, groups: groupIds });
    } catch (err) {
      console.error('Error updating user groups:', err);
      throw new Error(err instanceof Error ? err.message : 'Failed to update groups');
    }
  }, []);

  // Update super admin status
  const updateSuperAdmin = useCallback(async (userId: string, isSuperAdmin: boolean) => {
    try {
      await updateUserAccess({ userId, isSuperAdmin });
    } catch (err) {
      console.error('Error updating super admin status:', err);
      throw new Error(err instanceof Error ? err.message : 'Failed to update super admin status');
    }
  }, []);

  // Bulk update permissions
  const bulkUpdatePermissions = useCallback(async (update: BulkPermissionUpdate) => {
    try {
      const batch = writeBatch(db);
      
      update.userIds.forEach(userId => {
        const userRef = doc(db, 'system_users', userId);
        const user = users.find(u => u.id === userId);
        
        if (user) {
          const updatedPermissions = {
            ...user.permissions,
            ...update.permissions
          };

          batch.update(userRef, {
            permissions: updatedPermissions,
            updated_at: new Date()
          });
        }
      });

      await batch.commit();
    } catch (err) {
      console.error('Error bulk updating permissions:', err);
      throw new Error(err instanceof Error ? err.message : 'Failed to bulk update permissions');
    }
  }, [users]);

  // Bulk update roles
  const bulkUpdateRoles = useCallback(async (update: BulkRoleUpdate) => {
    try {
      const batch = writeBatch(db);
      
      update.userIds.forEach(userId => {
        const userRef = doc(db, 'system_users', userId);
        
        batch.update(userRef, {
          role: update.role,
          updated_at: new Date()
        });
      });

      await batch.commit();
    } catch (err) {
      console.error('Error bulk updating roles:', err);
      throw new Error(err instanceof Error ? err.message : 'Failed to bulk update roles');
    }
  }, []);

  // Associate user with Firebase Auth
  const associateUser = useCallback(async (userId: string, firebaseUid: string) => {
    try {
      const userRef = doc(db, 'system_users', userId);
      
      await updateDoc(userRef, {
        firebase_uid: firebaseUid,
        association_status: 'manual',
        updated_at: new Date()
      });

      // Note: Legacy auth user record update removed - using system_users only
      // const authUserRef = doc(db, 'users', firebaseUid);
      // const user = users.find(u => u.id === userId);
      //
      // if (user) {
      //   await updateDoc(authUserRef, {
      //     employee_id: user.employee_id,
      //     association_status: 'associated'
      //   });
      // }

    } catch (err) {
      console.error('Error associating user:', err);
      throw new Error(err instanceof Error ? err.message : 'Failed to associate user');
    }
  }, [users]);

  // Mark data collection as complete/incomplete
  const markDataCollectionComplete = useCallback(async (employeeId: string, isComplete: boolean) => {
    try {
      console.log('markDataCollectionComplete called with:', { employeeId, isComplete });
      console.log('Available users:', users.map(u => ({ id: u.id, employee_id: u.employee_id, email: u.email })));
      
      // Find user by employee_id instead of document id
      const user = users.find(u => u.employee_id.toString() === employeeId);
      
      if (!user) {
        console.error(`User with employee_id ${employeeId} not found in users array`);
        console.log('Available employee IDs:', users.map(u => u.employee_id));
        throw new Error(`User with employee_id ${employeeId} not found in system users`);
      }
      
      console.log('Found user:', { id: user.id, employee_id: user.employee_id, email: user.email });
      
      // Use the document ID for the Firestore reference
      const userRef = doc(db, 'system_users', user.id);
      
      const updateData: any = {
        data_collection_complete: isComplete,
        updated_at: new Date()
      };

      if (isComplete) {
        updateData.data_collection_completed_at = new Date();
      } else {
        // Remove completion timestamp if marking as incomplete
        updateData.data_collection_completed_at = null;
      }

      console.log('Attempting to update document with data:', updateData);
      await updateDoc(userRef, updateData);
      console.log('Successfully updated data collection status');
    } catch (err) {
      console.error('Error updating data collection status:', err);
      console.error('Error details:', {
        employeeId,
        isComplete,
        usersCount: users.length,
        errorMessage: err instanceof Error ? err.message : 'Unknown error',
        errorStack: err instanceof Error ? err.stack : undefined
      });
      throw new Error(err instanceof Error ? err.message : 'Failed to update data collection status');
    }
  }, [users]);

  // Toggle starred workflow
  const toggleStarredWorkflow = useCallback(async (userId: string, workflowId: string) => {
    try {
      const userRef = doc(db, 'system_users', userId);
      const user = users.find(u => u.id === userId);
      
      if (!user) {
        throw new Error('User not found');
      }

      const currentStarred = user.starred_workflows || [];
      const isCurrentlyStarred = currentStarred.includes(workflowId);
      
      const updatedStarred = isCurrentlyStarred
        ? currentStarred.filter(id => id !== workflowId)
        : [...currentStarred, workflowId];

      await updateDoc(userRef, {
        starred_workflows: updatedStarred,
        updated_at: new Date()
      });

    } catch (err) {
      console.error('Error toggling starred workflow:', err);
      throw new Error(err instanceof Error ? err.message : 'Failed to toggle starred workflow');
    }
  }, [users]);

  // Toggle starred user
  const toggleStarredUser = useCallback(async (userId: string, starredUserId: string) => {
    try {
      const userRef = doc(db, 'system_users', userId);
      const user = users.find(u => u.id === userId);
      
      if (!user) {
        throw new Error('User not found');
      }

      const currentStarred = user.starred_users || [];
      const isCurrentlyStarred = currentStarred.includes(starredUserId);
      
      const updatedStarred = isCurrentlyStarred
        ? currentStarred.filter((id: string) => id !== starredUserId)
        : [...currentStarred, starredUserId];

      await updateDoc(userRef, {
        starred_users: updatedStarred,
        updated_at: new Date()
      });

    } catch (err) {
      console.error('Error toggling starred user:', err);
      throw new Error(err instanceof Error ? err.message : 'Failed to toggle starred user');
    }
  }, [users]);

  // Toggle starred team
  const toggleStarredTeam = useCallback(async (userId: string, teamId: string) => {
    try {
      const userRef = doc(db, 'system_users', userId);
      const user = users.find(u => u.id === userId);
      
      if (!user) {
        throw new Error('User not found');
      }

      const currentStarred = user.starred_teams || [];
      const isCurrentlyStarred = currentStarred.includes(teamId);
      
      const updatedStarred = isCurrentlyStarred
        ? currentStarred.filter((id: string) => id !== teamId)
        : [...currentStarred, teamId];

      await updateDoc(userRef, {
        starred_teams: updatedStarred,
        updated_at: new Date()
      });

    } catch (err) {
      console.error('Error toggling starred team:', err);
      throw new Error(err instanceof Error ? err.message : 'Failed to toggle starred team');
    }
  }, [users]);

  // Update CRM saved view (for Companies table)
  const updateCrmSavedView = useCallback(async (userId: string, filters: any[]) => {
    try {
      const userRef = doc(db, 'system_users', userId);
      
      await updateDoc(userRef, {
        crm_saved_view: {
          filters,
          savedAt: new Date()
        },
        updated_at: new Date()
      });

    } catch (err) {
      console.error('Error updating CRM saved view:', err);
      throw new Error(err instanceof Error ? err.message : 'Failed to update CRM saved view');
    }
  }, []);

  // Update CRM saved view for People table
  const updateCrmSavedViewPeople = useCallback(async (userId: string, filters: any[]) => {
    try {
      const userRef = doc(db, 'system_users', userId);
      
      await updateDoc(userRef, {
        crm_saved_view_people: {
          filters,
          savedAt: new Date()
        },
        updated_at: new Date()
      });

    } catch (err) {
      console.error('Error updating CRM saved view for People:', err);
      throw new Error(err instanceof Error ? err.message : 'Failed to update CRM saved view for People');
    }
  }, []);

  // Calculate statistics
  const statistics = {
    totalUsers: users.length,
    associatedUsers: users.filter(u => u.association_status === 'associated').length,
    pendingUsers: users.filter(u => u.association_status === 'pending').length,
    manualUsers: users.filter(u => u.association_status === 'manual').length,
    adminUsers: users.filter(u => u.groups?.includes('admin')).length,
    managerGroupUsers: users.filter(u => u.groups?.includes('manager')).length,
    leadershipUsers: users.filter(u => u.groups?.includes('senior-managers')).length,
  };

  return {
    users,
    loading,
    error,
    totalUsers: users.length,
    filteredUsers,
    searchUsers,
    clearFilters,
    currentFilters,
    updateUserPermissions,
    updateUserRole,
    updateUserGroups,
    updateSuperAdmin,
    bulkUpdatePermissions,
    bulkUpdateRoles,
    associateUser,
    markDataCollectionComplete,
    toggleStarredWorkflow,
    toggleStarredUser,
    toggleStarredTeam,
    updateCrmSavedView,
    updateCrmSavedViewPeople,
    statistics
  };
}

